<template>
  <div
    v-if="view && view.strip"
    class="fu-dra-strip"
    role="status"
    :data-alert="alert.key"
    data-test="fu-drawer-alert"
  >
    <fu-icon name="alert" :size="15" class="fu-dra-strip__icon" />
    <span><strong>Last attempt.</strong> {{ view.body }}</span>
  </div>
  <div
    v-else-if="view"
    class="fu-dra"
    :class="`fu-dra--${view.tone}`"
    role="status"
    :data-alert="alert.key"
    data-test="fu-drawer-alert"
  >
    <fu-icon :name="view.icon" :size="16" class="fu-dra__icon" />
    <div class="fu-dra__content">
      <div class="fu-dra__title">{{ view.title }}</div>
      <p v-if="view.body" class="fu-dra__body">{{ view.body }}</p>
      <ul v-if="view.list.length" class="fu-dra__list" data-test="fu-drawer-alert-reasons">
        <li v-for="(li, i) in view.list" :key="i">{{ li }}</li>
      </ul>
      <button
        v-if="view.cta"
        type="button"
        class="fu-dra__cta"
        data-test="fu-drawer-alert-cta"
        @click="$emit('restart')"
      ><fu-icon name="restart" :size="15" />{{ view.cta }}</button>
    </div>
  </div>
</template>

<script>
/**
 * DrawerAlert — the ONE alert drawerAlerts.resolveDrawerAlert picks (the legacy chain's order). The
 * early-follow-up wizard is DrawerEarlyWizard, so `earlyWizard` renders nothing here; `lastAttempt`
 * is the design's amber strip above the form.
 *
 *   alert            resolveDrawerAlert() → { key, variant?, reasons? } or null
 *   totalAttempts    alertConditions().totalAttempts
 *   maxFollowups     alertConditions().maxFollowups (raw setting) — "All done — 4 of 4"
 *   nextAttemptDate  display string of the task's follow-up date, for "Already followed up today"
 *   assigneeName     the manual assignee's name when the not-responsible alert is a manual assignment
 *   canRestart       show "Restart follow-ups" on Do not follow up / All done (the container decides —
 *                    today's drawer has no restart; the table's popover does)
 * Copy: the design's titles; today's sentences and today's Do-not-follow-up reasons (from the rules).
 * Events: restart. Presentational.
 */
import FuIcon from '../FuIcon.vue';

const ALERT_COPY = {
  recallScheduled: { title: 'Recall appointment scheduled', body: 'Follow-up attempt is not allowed. The patient will disappear from the list when refreshed.' },
  notResponsible: { title: 'Not authorized', body: 'You are not the assignee for these follow-ups. Please update the settings or contact your administrator.' },
  assigned: { body: 'Only the assignee can log follow-ups here. Reassign it from the Follow-up column, or ask an admin.' },
  doNotFollowup: { title: 'Do not follow up', body: 'You cannot follow up because one or more of the following is true:' },
  maxAttempts: { body: 'You’ve made every follow-up your settings allow.' },
  alreadyToday: { title: 'Already followed up today', body: 'You have already made a follow-up today on this call.' },
};

export default {
  name: 'DrawerAlert',
  components: { FuIcon },
  props: {
    alert: { type: Object, default: null },
    totalAttempts: { type: [Number, String], default: 0 },
    maxFollowups: { type: [Number, String], default: null },
    nextAttemptDate: { type: String, default: '' },
    assigneeName: { type: String, default: '' },
    canRestart: { type: Boolean, default: false },
  },
  computed: {
    view() {
      const a = this.alert;
      if (!a || a.key === 'earlyWizard') return null;
      const max = parseInt(this.maxFollowups, 10);
      const n = Number(this.totalAttempts) || 0;
      const base = { list: [], cta: '', body: '', strip: false };
      switch (a.key) {
        case 'recallScheduled':
          return Object.assign(base, { tone: 'danger', icon: 'calx' }, ALERT_COPY.recallScheduled);
        case 'notResponsible':
          return this.assigneeName
            ? Object.assign(base, { tone: 'danger', icon: 'ban', title: `Assigned to ${this.assigneeName}`, body: ALERT_COPY.assigned.body })
            : Object.assign(base, { tone: 'danger', icon: 'ban' }, ALERT_COPY.notResponsible);
        case 'doNotFollowup':
          return Object.assign(base, { tone: 'danger', icon: 'ban' }, ALERT_COPY.doNotFollowup, {
            list: Array.isArray(a.reasons) ? a.reasons : [],
            cta: this.canRestart ? 'Restart follow-ups' : '',
          });
        case 'maxAttempts':
          return Object.assign(base, {
            tone: 'success',
            icon: 'check',
            title: Number.isFinite(max) ? `All done — ${Math.min(n, max)} of ${max} attempts made` : 'All done',
            body: ALERT_COPY.maxAttempts.body,
            cta: this.canRestart ? 'Restart follow-ups' : '',
          });
        case 'alreadyToday':
          return Object.assign(base, {
            tone: 'warn',
            icon: 'clock',
            title: ALERT_COPY.alreadyToday.title,
            body: this.nextAttemptDate ? `You can log the next attempt on ${this.nextAttemptDate}.` : ALERT_COPY.alreadyToday.body,
          });
        case 'lastAttempt':
          return Object.assign(base, {
            strip: true,
            body: Number.isFinite(max) ? `Your settings allow ${max} — this is the final one.` : 'This is the final one your settings allow.',
          });
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-dra {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
}

.fu-dra--danger { background: #fdf1f1; border-color: #f5d0d0; color: #8f2328; .fu-dra__icon { color: #c2373d; } }
.fu-dra--success { background: #eef8f3; border-color: #cde8dc; color: #0f5b42; .fu-dra__icon { color: $fu-success; } }
.fu-dra--warn { background: $fu-warning-subtle; border-color: #fbe5bf; color: #7a4d0c; .fu-dra__icon { color: $fu-warning; } }

.fu-dra__icon { flex: none; margin-top: 2px; }
.fu-dra__content { flex: 1; min-width: 0; }
.fu-dra__title { font-size: 14px; font-weight: $fu-fw-bold; }
.fu-dra__body { margin: 3px 0 0; font-size: 13px; line-height: 1.45; }
.fu-dra__list { margin: 6px 0 0; padding-left: 18px; font-size: 13px; line-height: 1.6; }

.fu-dra__cta {
  @include fu-btn(secondary, sm);
  margin-top: 12px;
}

.fu-dra-strip {
  @include fu-dr-warn-strip;
  strong { font-weight: $fu-fw-bold; }
}

.fu-dra-strip__icon { flex: none; margin-top: 1px; color: $fu-warning; }
</style>
